import Cookies from 'js-cookie';
import {
  getFormData,
  resetFormErrors,
  showFormErrors,
} from '../../utils/form';
import {handleCaptcha, initCaptcha, resetCaptcha} from '../../utils/captcha';
import {areAllPropertiesEmpty} from '../../utils/helpers';
import {reportJobAlertSubscriptionEvent} from '../../utils/analytics';
import {logErrorMessage} from '../../utils/errors';
import trans from '../../utils/trans';
import showInfoModal from '../../utils/info-modal';
import * as notification from '../../components/notifications';

const HIDE_COOKIE_NAME = 'jb-alerts-subscribe-hidden';
const HIDE_COOKIE_EXPIRATION_DAYS = 7;

const BELL_ICON_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">
  <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
  <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
</svg>`;

class JobsListAlertsSubscribe {
  constructor(section) {
    this.section = section;
    this.card = $('.jb-alerts-subscribe', this.section);
    this.modal = $('.jb-alerts-subscribe-modal', this.section);
    this.form = $('.jb-alerts-subscribe-modal-form', this.section);
    this.idAttributePrefix = this.section.attr('id') + '-';
    this.captchaId = this.idAttributePrefix + 'alerts-subscribe-captcha-container';
    this.fieldsGenerated = false;

    this._initHideButton();
    this._initOpenButton();
    this._initCaptcha();
  }

  _initHideButton() {
    $('.jb-alerts-subscribe-hide', this.card).click(() => {
      if (!window.$inBuilderMode) {
        Cookies.set(HIDE_COOKIE_NAME, '1', {
          path: '/',
          expires: HIDE_COOKIE_EXPIRATION_DAYS,
          secure: /https/.test(window.location.href),
        });
      }

      this._hideCard();
    });
  }

  // The card carries the d-flex class, which wins over d-none, so both are switched.
  _hideCard() {
    this.card.removeClass('d-flex').addClass('d-none');
  }

  _initOpenButton() {
    $('.jb-alerts-subscribe-open', this.card).click(() => {
      this._generateModalFields();
      this.modal.modal('show');
    });
  }

  /**
   * The modal content is intentionally not part of the page HTML,
   * it is generated here the first time the modal is opened.
   */
  _generateModalFields() {
    if (this.fieldsGenerated) {
      return;
    }

    const fieldsContainer = $('.jb-alerts-subscribe-modal-fields', this.form);
    const emailId = this.idAttributePrefix + 'alerts-subscribe-email';
    const frequencyId = this.idAttributePrefix + 'alerts-subscribe-frequency';

    // The fields take the global form styles of the theme, passed from the blade.
    const controlClasses = this.form.data('control-classes');
    const labelClasses = this.form.data('label-classes');

    const emailGroup = $('<div class="form-group text-left"></div>');
    emailGroup.append(
      $('<label></label>')
        .addClass(labelClasses)
        .attr('for', emailId)
        .text(this.form.data('email-label'))
    );
    emailGroup.append(
      $('<input type="email" name="email">')
        .addClass(controlClasses)
        .attr('id', emailId)
        .attr('placeholder', this.form.data('email-placeholder'))
    );

    const frequencyGroup = $('<div class="form-group text-left"></div>');
    frequencyGroup.append(
      $('<label></label>')
        .addClass(labelClasses)
        .attr('for', frequencyId)
        .text(this.form.data('frequency-label'))
    );

    const frequencySelect = $('<select name="alert_frequency"></select>')
      .addClass(controlClasses)
      .attr('id', frequencyId);
    const defaultFrequency = this.form.attr('data-default-frequency');

    (this.form.data('frequencies') || []).forEach((frequency) => {
      const isDefaultFrequency = frequency.value === defaultFrequency;

      frequencySelect.append(
        $('<option></option>')
          .attr('value', frequency.value)
          .prop('defaultSelected', isDefaultFrequency)
          .prop('selected', isDefaultFrequency)
          .text(frequency.label)
      );
    });

    frequencyGroup.append(frequencySelect);

    // The submit button uses the global primary button styles, passed from the blade.
    const submitButton = $('<button type="submit" class="btn-submit d-flex align-items-center justify-content-center"></button>')
      .addClass(this.form.data('submit-button-classes'))
      .append($(BELL_ICON_SVG).addClass('jb-alerts-subscribe-open-icon flex-shrink-0'))
      .append(document.createTextNode(' ' + (this.form.data('subscribe-text') || '')));

    fieldsContainer.append(emailGroup, frequencyGroup, submitButton);

    const tosText = this.form.data('tos');
    if (tosText) {
      // The ToS text is admin controlled HTML from the job board settings.
      fieldsContainer.append(
        $('<div class="jb-text-can-have-links jb-alerts-subscribe-modal-tos text-center"></div>').html(tosText)
      );
    }

    this.fieldsGenerated = true;
  }

  _initCaptcha() {
    initCaptcha(() => {
      this.subscribe();
    }, this.captchaId);

    this.form.submit(handleCaptcha(
      (e) => {
        this.subscribe();

        return false;
      }, this.captchaId
    ));
  }

  async subscribe() {
    const data = this.getAlertSubscriptionData();

    $('.btn-submit', this.form).loading();

    try {
      const response = await axios.post('/api/jobs/subscribe', data);

      resetFormErrors(this.form);

      this.modal.modal('hide');
      this.showSubscribedModal(response.data.message);

      reportJobAlertSubscriptionEvent();

      this.form.trigger('reset');

      $('.btn-submit', this.form).loading(false);
    } catch (e) {
      $('.btn-submit', this.form).loading(false);

      const response = e.response;
      const errors = response && response.data ? response.data.errors : null;

      if (!errors) {
        // Network failure or a response without a validation payload (offline, timeout, 5xx).
        notification.danger('Something went wrong, please reload this page and try again!');
        logErrorMessage('Alert Subscribe: Unexpected error response received', e);
      } else if (typeof errors.message !== 'undefined') {
        notification.danger(errors.message);
      } else {
        response.data.errors = this.mapErrorsToFormInputs(errors);

        if (!Object.keys(response.data.errors).length) {
          response.data.errors.email = ['Something went wrong, please reload this page and try again!'];
          logErrorMessage('Alert Subscribe: Unexpected error response received', e);
        }

        showFormErrors(this.form, response);
      }
    }

    resetCaptcha(this.captchaId);
  }

  /**
   * The API reports field errors with alert_condition.* keys, while the only
   * visible inputs are email and frequency. Errors without a matching input
   * fall back to the email field so the visitor always sees the problem.
   */
  mapErrorsToFormInputs(errors) {
    const mappedErrors = {};

    Object.keys(errors).forEach((errorKey) => {
      const inputName = errorKey.indexOf('alert_condition.') === 0
        ? errorKey.substring('alert_condition.'.length).split('.')[0]
        : errorKey;

      if ($(`[name="${inputName}"]`, this.form).length) {
        mappedErrors[inputName] = errors[errorKey];
      }
    });

    return mappedErrors;
  }

  getAlertSubscriptionData() {
    const formData = getFormData(this.form);

    let alertConditions = {};
    try {
      alertConditions = JSON.parse($('.jb-alerts-subscribe-filters', this.form).val());
    } catch (e) {
      logErrorMessage('Alert Subscribe: Failed to parse the filters of the jobs list card', e);
    }

    // An empty filters list is serialized as a JSON array, but the payload needs an object.
    if (Array.isArray(alertConditions) || alertConditions === null || typeof alertConditions !== 'object') {
      alertConditions = {};
    }

    const data = {
      email: formData.email,
      alert_frequency: formData.alert_frequency,
    };

    if (formData['g-recaptcha-response']) {
      data['g-recaptcha-response'] = formData['g-recaptcha-response'];
    }

    if (!areAllPropertiesEmpty(alertConditions)) {
      data.alert_condition = alertConditions;
    }

    return data;
  }

  showSubscribedModal(message) {
    const statusBox = $(`<div>
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-check2 text-success" viewBox="0 0 16 16">
          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
      </svg>
      ${message}
    </div>`);

    showInfoModal({
      title: trans('alert_subscription.success.title'),
      message: statusBox,
    });
  }
}

const initPage = () => {
  const section = $('.jb-section').has('.jb-alerts-subscribe').first();

  if (!section.length) {
    return;
  }

  new JobsListAlertsSubscribe(section.parent());
};

$(initPage);
